// src/pages/GenerateTemplateModal.js
//
// AI Source-Document template generator (MVP).
//
// Flow: upload the study protocol (PDF) -> store it -> call the
// generateSourceTemplate Cloud Function -> preview the draft -> save it as a
// DRAFT template (status: "draft") for review in the SectionEditor.
//
// The draft never auto-activates: it is saved with status "draft" and a
// coordinator must Approve it in the editor before it's considered final.

import { useState } from "react";
import { FUNCTIONS_BASE } from "../config/functionsBase";
import { authedFetch } from "../services/authedFetch";
import { getActiveTenantId, getActiveSiteId } from "../services/activeTenant";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, X, AlertTriangle, FileUp, Loader2, ScanText } from "lucide-react";
import { db, storage } from "../firebase";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { getSourcexTemplatesPath } from "../services/firestorePaths";
import { useDialog } from "../components/sourcex/useDialog";
import { prepareTemplateForWrite, makeTemplateVersion } from "../services/templateSchema";
import {
  pushTemplateVersion,
  migrateEmbeddedVersions,
} from "../services/templateVersionService";

const GEN_URL =
  `${FUNCTIONS_BASE}/generateSourceTemplate`;

const countDraft = (sections = []) => {
  let events = 0;
  let questions = 0;
  let eligibility = 0;
  sections.forEach((s) => {
    (s.events || []).forEach((ev) => {
      events += 1;
      questions += (ev.questions || []).length;
      if ((s.name || "").toLowerCase() === "eligibility")
        eligibility += (ev.questions || []).length;
    });
  });
  return { sections: sections.length, events, questions, eligibility };
};

export default function GenerateTemplateModal({ studyId, studyName, onClose }) {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [phase, setPhase] = useState("pick"); // pick | working | preview | saving
  const [statusText, setStatusText] = useState("");
  const [error, setError] = useState("");
  const [result, setResult] = useState(null); // { sections, meta }
  const { confirm: askConfirm, dialog } = useDialog();

  const handleGenerate = async () => {
    if (!file) return;
    setError("");
    setPhase("working");
    try {
      setStatusText("Uploading protocol…");
      const safeName = file.name.replace(/[^\w.\-]/g, "_");
      const path = `protocols/${studyId}/${Date.now()}_${safeName}`;
      const storageRef = ref(storage, path);
      await uploadBytes(storageRef, file, { contentType: file.type });
      const protocolUrl = await getDownloadURL(storageRef);

      setStatusText(
        "Reading the Schedule of Assessments and drafting a template… this can take up to a minute."
      );
      const resp = await authedFetch(GEN_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        // tenantId/siteId: the server checks membership against the site making
        // the request, not the one baked into the deployment.
        body: JSON.stringify({
          protocolUrl,
          tenantId: getActiveTenantId(),
          siteId: getActiveSiteId(),
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data.error || "Generation failed.");

      setResult({ ...data, protocolUrl });
      setPhase("preview");
    } catch (e) {
      setError(e.message || "Something went wrong.");
      setPhase("pick");
    }
  };

  const handleSave = async () => {
    if (!result) return;
    setError("");
    try {
      const tref = doc(db, getSourcexTemplatesPath(), studyId);
      const existing = await getDoc(tref);
      const existingData = existing.exists() ? existing.data() : null;
      const hasExistingContent = (existingData?.sections || []).length > 0;

      // A template opened before this fix may still carry the OLD embedded
      // versionHistory array, which is exactly what can push a document over
      // Firestore's 1 MiB limit. Move it to the versions subcollection and
      // clear the field before doing anything else, so this save can't be
      // blocked by history that has nothing to do with it.
      await migrateEmbeddedVersions(studyId, existingData?.versionHistory);

      if (hasExistingContent) {
        const ok = await askConfirm(
          existingData.status === "approved"
            ? "Saving will replace it with a new AI draft that you'll need to review and approve again. The current version is saved first, so it can be restored from Version History if needed."
            : "Saving will replace the current draft with a new AI draft. The current version is saved first, so it can be restored from Version History if needed.",
          {
            title:
              existingData.status === "approved"
                ? "This study already has an APPROVED template"
                : "This study already has a draft template",
            confirmLabel: "Replace it",
            tone: "danger",
          }
        );
        if (!ok) return;

        // Snapshot what's about to be overwritten BEFORE it's gone — this is
        // the whole point: a coordinator's manual edits to an existing draft,
        // or an already-approved template, must never just vanish because
        // someone re-ran the AI generator.
        await pushTemplateVersion(
          studyId,
          makeTemplateVersion({
            kind: "built",
            label: "Replaced by a new AI-generated draft",
            sections: existingData.sections,
          })
        );
      }

      setPhase("saving");
      // Normalize + strip before the AI draft ever lands in Firestore: assigns
      // stable question ids and guarantees the doc carries no answers.
      const clean = prepareTemplateForWrite({ sections: result.sections });
      await setDoc(
        tref,
        {
          studyId,
          sections: clean.sections,
          schemaVersion: clean.schemaVersion,
          headerFields: clean.headerFields,
          status: "draft",
          aiGenerated: true,
          protocolUrl: result.protocolUrl,
          aiModel: result.meta?.model || "",
          aiMethod: result.meta?.method || "",
          aiProvenanceNote: result.meta?.provenanceNote || "",
          aiTruncated: !!result.meta?.truncated,
          aiGeneratedAt: serverTimestamp(),
        },
        { merge: true }
      );
      onClose?.();
      navigate(`/sourcex/template/${studyId}`);
    } catch (e) {
      setError(e.message || "Could not save the draft.");
      setPhase("preview");
    }
  };

  const counts = result ? countDraft(result.sections) : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 font-[Manrope]">
      {/* Centered above this modal, rather than pinned to the top of the
          window the way a native confirm() is. */}
      {dialog}
      <motion.div
        initial={{ opacity: 0, y: 14, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ type: "spring", stiffness: 280, damping: 24 }}
        className="w-full max-w-lg rounded-2xl border border-white/10 bg-[#0b0820]/95 backdrop-blur-2xl shadow-2xl shadow-black/50 overflow-hidden text-slate-100"
      >
        {/* Header */}
        <div className="relative px-5 py-4 border-b border-white/10 bg-gradient-to-r from-purple-600/80 to-blue-600/80">
          <button
            onClick={onClose}
            className="absolute right-3 top-3 w-7 h-7 rounded-lg flex items-center justify-center text-white/80 hover:text-white hover:bg-white/10 transition"
          >
            <X className="w-4 h-4" />
          </button>
          <h2 className="text-base font-semibold flex items-center gap-2">
            <Sparkles className="w-4 h-4" /> Generate Source Template with AI
          </h2>
          <p className="text-xs text-white/80 mt-0.5">
            {studyName ? `${studyName} · ` : ""}
            {studyId}
          </p>
        </div>

        <div className="p-5">
          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, height: 0, marginBottom: 0 }}
                animate={{ opacity: 1, height: "auto", marginBottom: 12 }}
                exit={{ opacity: 0, height: 0, marginBottom: 0 }}
                className="overflow-hidden"
              >
                <div className="flex items-start gap-2 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>{error}</span>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* PICK */}
          {phase === "pick" && (
            <>
              <p className="text-sm text-slate-300 mb-3">
                Upload the study <strong className="text-white">protocol (PDF)</strong>.
                The AI reads it — including the Schedule of Assessments table — and drafts
                a source-document template for you to review and approve. It won't be
                active until you approve it.
              </p>
              <label className="block rounded-xl border border-dashed border-white/15 bg-white/[0.03] hover:bg-white/[0.05] hover:border-purple-300/40 transition cursor-pointer px-4 py-6 text-center">
                <FileUp className="w-6 h-6 mx-auto mb-2 text-purple-300" />
                <span className="text-sm text-slate-300">
                  {file ? file.name : "Click to choose the protocol PDF"}
                </span>
                <input
                  type="file"
                  accept="application/pdf"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  className="hidden"
                />
              </label>

              <div className="source-ai-warning mt-3 rounded-lg bg-amber-400/10 border border-amber-400/30 px-3 py-2 text-xs text-amber-200/90">
                AI can make mistakes. Use this as a helper, not the final word: treat the result as a first draft and verify every
                item against the protocol before approving.
              </div>
            </>
          )}

          {/* WORKING */}
          {phase === "working" && (
            <div className="py-10 text-center">
              <Loader2 className="w-9 h-9 mx-auto mb-4 text-purple-300 animate-spin" />
              <p className="text-sm text-slate-300">{statusText}</p>
            </div>
          )}

          {/* PREVIEW */}
          {(phase === "preview" || phase === "saving") && counts && (
            <>
              <p className="text-sm text-slate-300 mb-3">
                Draft ready. Review the structure, then save it to edit and approve.
              </p>
              <div className="grid grid-cols-4 gap-2 mb-3">
                {[
                  ["Sections", counts.sections],
                  ["Events", counts.events],
                  ["Fields", counts.questions],
                  ["Eligibility", counts.eligibility],
                ].map(([label, n]) => (
                  <div
                    key={label}
                    className="rounded-xl bg-white/[0.04] border border-white/10 p-2 text-center"
                  >
                    <div className="text-xl font-bold text-white">{n}</div>
                    <div className="text-[10px] uppercase tracking-wide text-slate-400">
                      {label}
                    </div>
                  </div>
                ))}
              </div>

              {result.meta?.visitCount != null && (
                <div className="mb-3 flex items-start gap-2 rounded-lg bg-blue-500/10 border border-blue-400/30 px-3 py-2 text-xs text-blue-200">
                  <ScanText className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>
                    Found <strong>{result.meta.visitCount}</strong> visit(s) and{" "}
                    <strong>{result.meta.procedureCount}</strong> procedure(s) in the
                    Schedule of Assessments
                    {result.meta.soaPage ? ` (${result.meta.soaPage})` : ""}.
                    {result.meta.visitsAddedByVerification > 0 &&
                      ` +${result.meta.visitsAddedByVerification} recovered by the completeness check.`}
                  </span>
                </div>
              )}
              {result.meta?.method?.startsWith("vision") && (
                <div className="mb-3 flex items-start gap-2 rounded-lg bg-purple-500/10 border border-purple-400/30 px-3 py-2 text-xs text-purple-200">
                  <ScanText className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>
                    {result.meta.method === "vision-hires" ? (
                      <>
                        Read with high-resolution page vision — the Schedule of Assessments
                        table
                        {Array.isArray(result.meta.soaPagesRead) &&
                        result.meta.soaPagesRead.length
                          ? ` (page${result.meta.soaPagesRead.length > 1 ? "s" : ""} ${result.meta.soaPagesRead.join(", ")})`
                          : ""}{" "}
                        was parsed page-by-page for accurate visit/procedure selections (the
                        "X" marks).
                      </>
                    ) : (
                      <>
                        Read with document vision — the Schedule of Assessments table was
                        parsed visually for more accurate visits &amp; procedures.
                      </>
                    )}
                  </span>
                </div>
              )}
              {result.meta?.truncated && (
                <div className="mb-3 rounded-lg bg-amber-400/10 border border-amber-400/30 px-3 py-2 text-xs text-amber-200">
                  The protocol was long and was partially truncated — double-check later
                  visits/sections are complete.
                </div>
              )}
              {result.meta?.provenanceNote && (
                <div className="mb-3 rounded-lg bg-white/[0.04] border border-white/10 px-3 py-2 text-xs text-slate-300">
                  <span className="font-semibold text-slate-200">What the AI used: </span>
                  {result.meta.provenanceNote}
                </div>
              )}

              <div className="max-h-44 overflow-y-auto rounded-xl border border-white/10 divide-y divide-white/5">
                {result.sections.map((s, i) => (
                  <div key={i} className="px-3 py-2">
                    <div className="text-sm font-medium text-white">{s.name}</div>
                    <div className="text-xs text-slate-400">
                      {(s.events || []).map((e) => e.eventName).join(" · ") || "—"}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-white/10 bg-white/[0.02] flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm text-slate-300 hover:bg-white/[0.06] transition"
          >
            Cancel
          </button>
          {phase === "pick" && (
            <motion.button
              whileTap={{ scale: 0.97 }}
              onClick={handleGenerate}
              disabled={!file}
              className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-gradient-to-r from-purple-600 to-blue-600 hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed transition"
            >
              Generate Draft
            </motion.button>
          )}
          {(phase === "preview" || phase === "saving") && (
            <motion.button
              whileTap={{ scale: 0.97 }}
              onClick={handleSave}
              disabled={phase === "saving"}
              className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-gradient-to-r from-emerald-500 to-teal-500 hover:brightness-110 disabled:opacity-40 transition"
            >
              {phase === "saving" ? "Saving…" : "Save draft & review"}
            </motion.button>
          )}
        </div>
      </motion.div>
    </div>
  );
}
